import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Bot,
  Brain,
  Check,
  Code,
  Database,
  Globe,
  Key,
  Languages,
  LineChart,
  Lock,
  LogIn,
  Menu,
  MessageSquare,
  Play,
  Send,
  Settings,
  Shield,
  Sparkles,
  Users,
  Webhook,
  X,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from './hooks/useLanguage';
import { CookieConsentBanner } from '../../components/CookieConsentBanner';
import { Language } from './i18n/translations';
import { DynamicPricing } from './components/DynamicPricing';
import { getApiBase } from '../../lib/utils';

type LandingPageProps = {
  initialLanguage?: Language;
  onLanguageChange?: (lang: Language) => void;
};

type LandingDemoAgent = {
  agent_id: number;
  label: string;
  agent_name: string;
  subtitle?: string | null;
  welcome_title?: string | null;
  quick_questions?: string[];
  use_agent_avatar?: boolean;
  avatar_url?: string | null;
};

function landingDemoAvatarSrc(agent: LandingDemoAgent | null): string | null {
  if (!agent?.use_agent_avatar || !agent.avatar_url) return null;
  const base = getApiBase().replace(/\/$/, '');
  const path = agent.avatar_url.startsWith('/') ? agent.avatar_url : `/${agent.avatar_url}`;
  return `${base}${path}`;
}

export default function LandingPage({ initialLanguage, onLanguageChange }: LandingPageProps) {
  const { t, language, setLanguage } = useLanguage({ preferredLanguage: initialLanguage });
  const [mode, setMode] = useState<'pme' | 'agence'>('pme');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [brandingLogo, setBrandingLogo] = useState<string | null>(null);
  const [landingDemoAgents, setLandingDemoAgents] = useState<LandingDemoAgent[]>([]);
  const [demoAgentId, setDemoAgentId] = useState<number | null>(null);
  const [demoInput, setDemoInput] = useState('');
  const [demoMessages, setDemoMessages] = useState<Array<{ type: 'user' | 'agent'; text: string }>>([]);
  const [demoSending, setDemoSending] = useState(false);
  const [demoStatus, setDemoStatus] = useState<string | null>(null);

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    onLanguageChange?.(lang);
    setLangOpen(false);
  };

  const languageOptions: Array<{ code: Language; label: string; flag: string }> = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
  ];

  const currentLanguageOption = languageOptions.find((item) => item.code === language) ?? languageOptions[1];

  useEffect(() => {
    let cancelled = false;
    const apiBase = getApiBase().replace(/\/$/, '');

    const loadBrandingLogo = async () => {
      try {
        const fromStorage = localStorage.getItem('branding_logo_url');
        if (fromStorage && !cancelled) {
          setBrandingLogo(fromStorage);
        }
      } catch {
        // Ignore storage errors.
      }

      try {
        const response = await fetch(`${apiBase}/branding/config`);
        if (!response.ok) return;
        const data = await response.json();
        const raw = data?.logo_url as string | null | undefined;
        if (!raw || cancelled) return;
        const normalized = raw.startsWith('/') ? raw : `/${raw}`;
        const absolute = `${apiBase}${normalized}`;
        if (!cancelled) {
          setBrandingLogo(absolute);
        }
      } catch {
        // Keep fallback if API unavailable.
      }
    };

    loadBrandingLogo();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const apiBase = getApiBase().replace(/\/$/, '');

    const loadLandingDemoAgents = async () => {
      try {
        const response = await fetch(`${apiBase}/global-config/public/landing-demo-agents`);
        if (!response.ok) return;
        const data = await response.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        if (cancelled) return;
        setLandingDemoAgents(items);
        if (items.length > 0) {
          setDemoAgentId(items[0].agent_id);
          setDemoMessages([]);
        }
      } catch {
        if (!cancelled) {
          setLandingDemoAgents([]);
          setDemoAgentId(null);
        }
      }
    };

    loadLandingDemoAgents();
    return () => {
      cancelled = true;
    };
  }, []);

  const navLinks = useMemo(
    () => [
      { href: '#produit', label: t.nav.product },
      { href: '#usecases', label: t.nav.useCases },
      { href: '#agences', label: t.nav.agencies },
      { href: '#securite', label: t.nav.security },
      { href: '#prix', label: t.nav.pricing },
      { href: '#faq', label: t.nav.faq },
    ],
    [t]
  );

  const socialProof = useMemo(
    () => [
      { metric: t.socialProof.widgetMetric, label: t.socialProof.widget, icon: Code },
      { metric: t.socialProof.creationMetric, label: t.socialProof.creation, icon: Zap },
      { metric: t.socialProof.ragMetric, label: t.socialProof.ragImport, icon: Database },
      { metric: t.socialProof.productionMetric, label: t.socialProof.production, icon: LineChart },
    ],
    [t]
  );

  const featureCards = useMemo(
    () => [
      { ...t.features.agents, icon: Bot, badge: 'Core' },
      { ...t.features.rag, icon: Database, badge: 'Core' },
      { ...t.features.tools, icon: Webhook, badge: 'Pro+' },
      { ...t.features.deployment, icon: Globe, badge: 'Core' },
      { ...t.features.observability, icon: LineChart, badge: 'Pro+' },
      { ...t.features.multiWorkspace, icon: Users, badge: 'Agency' },
    ],
    [t]
  );

  const useCases = mode === 'pme' ? t.useCases.pme : t.useCases.agency;
  const useCaseValues = Object.values(useCases);

  const selectedDemoAgent = landingDemoAgents.find((agent) => agent.agent_id === demoAgentId) || null;

  const handleSwitchDemoAgent = (agentId: number) => {
    setDemoAgentId(agentId);
    setDemoMessages([]);
  };

  const demoWelcomeText = useMemo(() => {
    if (!selectedDemoAgent) return '';
    const custom = selectedDemoAgent.welcome_title?.trim();
    if (custom) return custom;
    return t.demo.defaultWelcome.replace('{name}', selectedDemoAgent.agent_name);
  }, [selectedDemoAgent, t.demo.defaultWelcome]);

  const demoQuickQuestions = selectedDemoAgent?.quick_questions?.filter((q) => q.trim()) ?? [];
  const demoHasUserMessages = demoMessages.some((m) => m.type === 'user');
  const demoAvatar = landingDemoAvatarSrc(selectedDemoAgent);

  const sendDemoMessage = async (rawText: string) => {
    const value = rawText.trim();
    if (!value || !selectedDemoAgent || demoSending) return;
    setDemoInput('');
    setDemoMessages((prev) => [...prev, { type: 'user', text: value }]);
    setDemoSending(true);
    setDemoStatus(t.demo.statusReplying);

    try {
      const response = await fetch(`${getApiBase().replace(/\/$/, '')}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: value,
          agent_id: selectedDemoAgent.agent_id,
          with_sources: false,
          source: 'widget_external',
        }),
      });

      if (!response.ok || !response.body) {
        const payload = await response.json().catch(() => ({}));
        const detail = typeof payload?.detail === 'string' ? payload.detail : t.demo.errorCouldNotReply;
        setDemoMessages((prev) => [...prev, { type: 'agent', text: detail }]);
        setDemoStatus(null);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullAnswer = '';
      let firstTokenReceived = false;

      while (true) {
        const { value: chunk, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(chunk, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const eventData = JSON.parse(line.slice(6));

          if (eventData.status_message && !firstTokenReceived) {
            setDemoStatus(eventData.status_message);
          }
          if (eventData.error) {
            setDemoMessages((prev) => [...prev, { type: 'agent', text: eventData.error }]);
            setDemoStatus(null);
            return;
          }
          if (eventData.token) {
            fullAnswer += eventData.token;
            if (!firstTokenReceived) {
              firstTokenReceived = true;
              setDemoStatus(null);
              setDemoMessages((prev) => [...prev, { type: 'agent', text: eventData.token }]);
            } else {
              setDemoMessages((prev) => {
                const next = [...prev];
                const lastIndex = next.length - 1;
                if (lastIndex >= 0 && next[lastIndex].type === 'agent') {
                  next[lastIndex] = { ...next[lastIndex], text: fullAnswer };
                }
                return next;
              });
            }
          }
        }
      }

      if (!fullAnswer.trim()) {
        setDemoMessages((prev) => [...prev, { type: 'agent', text: t.demo.errorCouldNotReply }]);
      }
    } catch {
      setDemoMessages((prev) => [...prev, { type: 'agent', text: t.demo.errorDemoUnavailable }]);
    } finally {
      setDemoStatus(null);
      setDemoSending(false);
    }
  };

  const handleDemoSend = () => {
    void sendDemoMessage(demoInput);
  };

  return (
    <div className="bg-zinc-950 min-h-screen text-zinc-50 selection:bg-emerald-500/30">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-zinc-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {brandingLogo ? (
              <img src={brandingLogo} alt="Totemium logo" className="h-6 w-6 rounded object-contain" />
            ) : (
              <Bot className="h-5 w-5 text-emerald-400" />
            )}
            <span className="font-bold">Totemium</span>
          </div>
          <nav className="hidden items-center gap-5 md:flex">
            {navLinks.map((item) => (
              <a key={item.href} href={item.href} className="text-sm text-zinc-300 hover:text-white">
                {item.label}
              </a>
            ))}
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            <div className="relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="inline-flex items-center rounded-full border border-white/20 px-3 py-1.5 text-sm"
              >
                <span>{currentLanguageOption.flag}</span>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full z-20 mt-2 w-40 rounded-xl border border-white/15 bg-zinc-900 p-1 shadow-xl">
                  {languageOptions.map((item) => (
                    <button
                      key={item.code}
                      onClick={() => handleLanguageChange(item.code)}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm ${
                        language === item.code ? 'bg-emerald-500/15 text-emerald-300' : 'text-zinc-200 hover:bg-white/5'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span>{item.flag}</span>
                        <span>{item.label}</span>
                      </span>
                      <span className="text-[10px] uppercase opacity-80">{item.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-full border border-white/20 p-1">
              <button
                onClick={() => setMode('pme')}
                className={`rounded-full px-3 py-1 text-xs ${mode === 'pme' ? 'bg-emerald-500 text-black' : 'text-zinc-300'}`}
              >
                {t.toggle.pme}
              </button>
              <button
                onClick={() => setMode('agence')}
                className={`rounded-full px-3 py-1 text-xs ${mode === 'agence' ? 'bg-violet-500 text-white' : 'text-zinc-300'}`}
              >
                {t.toggle.agency}
              </button>
            </div>
            <Link to="/login" className="text-sm text-zinc-300 hover:text-white">{t.navbar.login}</Link>
            <Link to="/register" className="rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-black">{t.cta.startFree}</Link>
          </div>
          <div className="flex items-center gap-2 md:hidden">
            <Link
              to="/login"
              aria-label={t.navbar.login}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-zinc-200"
            >
              <LogIn className="h-4 w-4" />
            </Link>
            <button onClick={() => setMobileOpen((v) => !v)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="space-y-2 border-t border-white/10 px-4 py-3 md:hidden">
            <div className="mb-2 flex gap-2">
              {languageOptions.map((item) => (
                <button
                  key={item.code}
                  onClick={() => handleLanguageChange(item.code)}
                  className={`rounded-full border px-3 py-1.5 text-xs ${
                    language === item.code
                      ? 'border-emerald-400 bg-emerald-500/15 text-emerald-300'
                      : 'border-white/20 text-zinc-300'
                  }`}
                >
                  {item.flag} {item.code.toUpperCase()}
                </button>
              ))}
            </div>
            {navLinks.map((item) => (
              <a key={item.href} href={item.href} className="block text-sm text-zinc-300" onClick={() => setMobileOpen(false)}>
                {item.label}
              </a>
            ))}
          </div>
        )}
      </header>
      <main>
        <section className="mx-auto max-w-7xl px-4 pb-20 pt-20 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-emerald-300">
            <Sparkles className="h-4 w-4" />
            {mode === 'pme' ? t.hero.badgePme : t.hero.badgeAgency}
          </span>
          <h1 className="mx-auto mt-6 max-w-5xl text-4xl font-bold sm:text-6xl">
            {t.hero.headline}{' '}
            <span className={mode === 'agence' ? 'text-violet-400' : 'text-emerald-400'}>{t.hero.headlineHighlight}</span>
            {t.hero.headlineSuffix}
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg text-zinc-300">{mode === 'pme' ? t.hero.subheadPme : t.hero.subheadAgency}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" className="rounded-full bg-emerald-500 px-6 py-3 font-semibold text-black">{t.cta.startFree}</Link>
            <a href="#demo" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3"><Play className="h-4 w-4" />{t.cta.watchDemo}</a>
          </div>
        </section>

        <section className="border-y border-white/10 bg-white/5 py-10">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 md:grid-cols-4">
            {socialProof.map((item) => (
              <div key={item.label} className="text-center">
                <item.icon className="mx-auto mb-2 h-5 w-5 text-emerald-400" />
                <p className="text-2xl font-bold">{item.metric}</p>
                <p className="text-sm text-zinc-400">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="produit" className="mx-auto max-w-7xl px-4 py-16">
          <h2 className="text-3xl font-bold">{t.problemSolution.title} <span className="text-emerald-400">{t.problemSolution.titleHighlight}</span></h2>
          <p className="mt-3 text-zinc-400">{t.problemSolution.subtitle}</p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <article className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <Brain className="h-5 w-5 text-emerald-400" />
              <p className="mt-2 text-xs uppercase text-emerald-400">{t.problemSolution.respond.subtitle}</p>
              <h3 className="text-xl font-semibold">{t.problemSolution.respond.title}</h3>
              <p className="mt-2 text-sm text-zinc-400">{t.problemSolution.respond.description}</p>
            </article>
            <article className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <Settings className="h-5 w-5 text-violet-400" />
              <p className="mt-2 text-xs uppercase text-violet-400">{t.problemSolution.act.subtitle}</p>
              <h3 className="text-xl font-semibold">{t.problemSolution.act.title}</h3>
              <p className="mt-2 text-sm text-zinc-400">{t.problemSolution.act.description}</p>
            </article>
            <article className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <Globe className="h-5 w-5 text-emerald-400" />
              <p className="mt-2 text-xs uppercase text-emerald-400">{t.problemSolution.deploy.subtitle}</p>
              <h3 className="text-xl font-semibold">{t.problemSolution.deploy.title}</h3>
              <p className="mt-2 text-sm text-zinc-400">{t.problemSolution.deploy.description}</p>
            </article>
          </div>
        </section>

        <section id="usecases" className="mx-auto max-w-7xl px-4 py-16">
          <h2 className="text-3xl font-bold">{mode === 'pme' ? t.useCases.titlePme : t.useCases.titleAgency}</h2>
          <div className="mt-6 flex gap-2">
            <button onClick={() => setMode('pme')} className={`rounded-full px-4 py-2 text-sm ${mode === 'pme' ? 'bg-emerald-500 text-black' : 'border border-white/20 text-zinc-300'}`}>{t.toggle.pme}</button>
            <button id="agences" onClick={() => setMode('agence')} className={`rounded-full px-4 py-2 text-sm ${mode === 'agence' ? 'bg-violet-500 text-white' : 'border border-white/20 text-zinc-300'}`}>{t.toggle.agency}</button>
          </div>
          <div className={`mt-6 grid gap-4 ${mode === 'pme' ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
            {useCaseValues.map((uc) => (
              <article key={uc.title} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <h3 className="text-lg font-semibold">{uc.title}</h3>
                <p className="mt-2 text-sm text-zinc-400">{uc.description}</p>
              </article>
            ))}
          </div>
        </section>

        {landingDemoAgents.length > 0 && (
        <section id="demo" className="mx-auto max-w-4xl px-4 py-16">
          <h2 className="text-center text-3xl font-bold">{t.demo.title}</h2>
          <p className="mt-2 text-center text-zinc-400">{t.demo.subtitle}</p>
          <div className="mt-6 flex justify-center gap-2">
            {landingDemoAgents.map((agent, index) => (
              <button
                key={agent.agent_id}
                onClick={() => handleSwitchDemoAgent(agent.agent_id)}
                className={`rounded-full px-3 py-1.5 text-sm ${
                  demoAgentId === agent.agent_id
                    ? index % 2 === 0 ? 'bg-emerald-500 text-black' : 'bg-violet-500 text-white'
                    : 'border border-white/20'
                }`}
              >
                {agent.label}
              </button>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-white/10 bg-zinc-900">
            <div className="h-72 space-y-3 overflow-y-auto p-4">
              {selectedDemoAgent && !demoHasUserMessages && !demoSending ? (
                <div className="flex flex-col items-center justify-start gap-4 py-2 text-center">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                    {demoAvatar ? (
                      <img src={demoAvatar} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Bot className="h-8 w-8 text-emerald-400" />
                    )}
                  </div>
                  <p className="max-w-md text-sm leading-relaxed text-zinc-200">{demoWelcomeText}</p>
                  {demoQuickQuestions.length > 0 && (
                    <div className="flex w-full max-w-lg flex-wrap justify-center gap-2">
                      {demoQuickQuestions.map((q, qi) => (
                        <button
                          key={`${qi}-${q.slice(0, 24)}`}
                          type="button"
                          onClick={() => void sendDemoMessage(q)}
                          className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-left text-xs text-zinc-200 transition hover:border-emerald-500/40 hover:bg-emerald-500/10"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {demoMessages.map((m, idx) => (
                    <div key={idx} className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.type === 'user' ? 'ml-auto bg-emerald-500 text-black' : 'bg-white/10 text-zinc-100'}`}>
                      {m.text}
                    </div>
                  ))}
                  {demoSending && (
                    <div className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-4 py-2 text-xs text-zinc-300">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                      <span>{demoStatus || t.demo.statusReplying}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2 border-t border-white/10 p-3">
              <input
                value={demoInput}
                onChange={(e) => setDemoInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleDemoSend()}
                placeholder={t.demo.placeholder}
                className="flex-1 rounded-xl border border-white/20 bg-transparent px-3 py-2 text-sm outline-none"
              />
              <button type="button" onClick={handleDemoSend} className="rounded-xl bg-emerald-500 px-3 text-black">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>
        )}

        <section className="mx-auto max-w-7xl px-4 py-16">
          <h2 className="text-center text-3xl font-bold">{t.features.title}</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {featureCards.map((f) => (
              <article key={f.title} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="mb-4 flex items-center justify-between">
                  <f.icon className="h-5 w-5 text-emerald-400" />
                  <span className="rounded-full border border-white/20 px-2 py-0.5 text-xs text-zinc-300">{f.badge}</span>
                </div>
                <h3 className="text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-zinc-400">{f.description}</p>
              </article>
            ))}
          </div>
        </section>

        <DynamicPricing t={t} lang={language} />

        <section id="securite" className="mx-auto grid max-w-7xl gap-6 px-4 py-16 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-bold">{t.security.title} <span className="text-violet-400">{t.security.titleHighlight}</span></h2>
            <p className="mt-3 text-zinc-400">{t.security.description}</p>
            <a href="#demo" className="mt-6 inline-flex items-center gap-2 rounded-full bg-violet-500 px-5 py-2.5 font-semibold text-white">
              <Shield className="h-4 w-4" />
              {t.cta.talkSecurity}
            </a>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.values(t.security.features).map((item) => (
              <article key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <h3 className="font-semibold">{item.title}</h3>
                <p className="mt-1 text-sm text-zinc-400">{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="faq" className="mx-auto max-w-4xl px-4 py-16">
          <h2 className="text-center text-3xl font-bold">{t.faq.title}</h2>
          <div className="mt-8 space-y-3">
            {t.faq.items.map((item: any) => (
              <details key={item.q} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <summary className="cursor-pointer font-medium">{item.q}</summary>
                <p className="mt-2 whitespace-pre-line text-sm text-zinc-400">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-4 pb-20 pt-8 text-center">
          <h2 className="text-3xl font-bold">{t.finalCta.title}</h2>
          <p className="mt-3 text-zinc-400">{t.finalCta.description}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 font-semibold text-black">
              {t.cta.startFree}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#demo" className="rounded-full border border-white/20 px-6 py-3">{t.cta.requestDemo}</a>
          </div>
          <p className="mt-3 text-xs text-zinc-500">{t.finalCta.note}</p>
        </section>
      </main>
      <footer className="border-t border-white/10 py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 text-sm text-zinc-400 md:flex-row">
          <span className="inline-flex items-center gap-2">
            {brandingLogo ? (
              <img src={brandingLogo} alt="Totemium logo" className="h-5 w-5 rounded object-contain" />
            ) : (
              <Bot className="h-4 w-4 text-emerald-400" />
            )}
            <span>© {new Date().getFullYear()} Totemium. {t.footer.copyright}</span>
          </span>
          <div className="flex gap-4">
            <a href="#securite">{t.footer.security}</a>
            <a href="/cookies-policy">{t.footer.privacy}</a>
            <a href="#demo">{t.footer.contact}</a>
          </div>
        </div>
      </footer>
      <CookieConsentBanner language={language} />
    </div>
  );
}
